import { FC, ReactElement, useEffect, useRef } from "react";

interface Props {
  header: ReactElement | string;
  expanded: boolean;
  onExpandToggle: (visible: boolean) => void;
  className?: string;
  disabled?: boolean;
}

const Accordion: FC<Props> = ({
  header,
  children,
  expanded,
  onExpandToggle,
  className,
  disabled,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const panel = panelRef.current as HTMLDivElement;

    // Added so that panel size updates in case its content grows in height,
    // e.g when using another accordion inside an accordion panel like registration
    // form on checkout page
    const resizeObserver = new ResizeObserver(() => {
      if (panel.style.maxHeight) {
        const maxHeight = parseFloat(panel.style.maxHeight);
        if (panel.scrollHeight > maxHeight) {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
        }
      }
    });

    if (expanded) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
      if (panel.firstElementChild) {
        resizeObserver.observe(panel.firstElementChild);
      }
    } else {
      panel.style.maxHeight = "";
      resizeObserver.disconnect();
    }

    return () => resizeObserver.disconnect();
  }, [expanded]);

  return (
    <div className={className ?? ""}>
      <div
        className={disabled ? "" : "cursor-pointer"}
        onClick={() => {
          if (!disabled) {
            onExpandToggle(!expanded);
          }
        }}
      >
        {header}
      </div>
      <div
        ref={panelRef}
        className="transition-max-h duration-200 ease-out max-h-0 overflow-hidden"
      >
        {/* The div wrapper exists so it's easier to work with resize observer --
        do not remove without updating the observer as well  */}
        <div>{children}</div>
      </div>
    </div>
  );
};

export default Accordion;
